'use client'

import { Cell, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { EducationType } from '@marketplace-web/conversation-education/conversation-education-data'

import ProgressStepIconExperimental from '../../ProgressStepIconExperimental'

type Props = {
  type: EducationType
  price: string | null
}

type Step = {
  completed: boolean
  active: boolean
  title: string
  description: string
  testId?: string
}

const Steps = ({ type, price }: Props) => {
  const translate = useTranslate('conversation.education')

  const isTransactionCompleted = [
    EducationType.InConversationTransactionCompleted,
    EducationType.InConversationTransactionCompletedHAOV,
  ].includes(type)

  const isAuthenticityCheckVisible = [
    EducationType.InConversationSoldHAOV,
    EducationType.InConversationTransactionCompletedHAOV,
  ].includes(type)

  const formatSoldStep = () => {
    let description = translate('steps.sold.description', { price })
    let testId = 'education-sold-step'

    if (isAuthenticityCheckVisible) {
      description = translate('steps.sold.description_haov', { price })
      testId = 'education-sold-step-haov'
    }

    if (isTransactionCompleted) {
      description = translate('steps.sold.completed_description')
      testId = 'education-sold-step-completed'
    }

    return {
      completed: true,
      active: false,
      title: translate('steps.sold.title'),
      description,
      testId,
    }
  }

  const formatSendItStep = () => {
    let description = translate('steps.send_it.description')
    let testId = 'education-send-it-step'

    if (isTransactionCompleted) {
      description = translate('steps.send_it.completed_description')
      testId = 'education-send-it-step-completed'
    }

    if (isTransactionCompleted && isAuthenticityCheckVisible) {
      description = translate('steps.send_it.completed_description_haov')
      testId = 'education-send-it-step-completed-haov'
    }

    return {
      completed: isTransactionCompleted,
      active: !isTransactionCompleted,
      title: translate('steps.send_it.title'),
      description,
      testId,
    }
  }

  const formatAuthenticityCheckStep = () => ({
    completed: isTransactionCompleted,
    active: false,
    title: translate('steps.authenticity_check.title'),
    description: isTransactionCompleted
      ? translate('steps.authenticity_check.completed_description')
      : translate('steps.authenticity_check.description'),
    testId: 'education-haov-step',
  })

  const formatGetPaidStep = () => ({
    completed: false,
    active: isTransactionCompleted,
    title: translate('steps.get_paid.title'),
    description: isTransactionCompleted
      ? translate('steps.get_paid.completed_description')
      : translate('steps.get_paid.description'),
    testId: 'education-get-paid-step',
  })

  const saleSteps = [
    formatSoldStep(),
    formatSendItStep(),
    ...(isAuthenticityCheckVisible ? [formatAuthenticityCheckStep()] : []),
    formatGetPaidStep(),
  ]

  const getStepIconStatus = (completed: boolean, active: boolean) => {
    if (completed) return ProgressStepIconExperimental.Status.Completed

    if (active) return ProgressStepIconExperimental.Status.Active

    return ProgressStepIconExperimental.Status.Inactive
  }

  const renderStepIcon = (completed: boolean, active: boolean, stepNumber: number) => (
    <ProgressStepIconExperimental
      status={getStepIconStatus(completed, active)}
      stepNumber={stepNumber}
    />
  )

  const renderStep = (step: Step, index: number) => {
    const textTheme = step.active || step.completed ? 'inherit' : 'muted'

    return (
      <Cell
        key={index}
        theme="transparent"
        prefix={renderStepIcon(step.completed, step.active, index + 1)}
        title={<Text as="span" text={step.title} theme={textTheme} testId={step.testId} />}
        body={<Text as="span" text={step.description} theme={textTheme} />}
      />
    )
  }

  const renderSaleSteps = () => saleSteps.map(renderStep)

  return <>{renderSaleSteps()}</>
}

export default Steps
